import type {EntityPatch} from '../types';

/**
 * 草图组运行时历史快照。
 *
 * 设计说明：
 * 1. 当前项目尚未完成草图实体、约束、尺寸的完整 normalized CadDocument；
 * 2. 草图绘制、删除、裁剪会同时影响几何实体、控制点、约束容器与封闭轮廓；
 * 3. 因此现阶段使用“草图组运行时快照 ID + 签名”进入会话历史，真实 Three.js 克隆体只保存在当前页面运行时注册表中。
 */
export type SketchGroupRuntimeDisplayState = 'edit' | 'shown' | 'hidden';

export interface SketchGroupRuntimeHistorySnapshot {
    /** 草图组 ID。 */
    readonly sketchId: string;
    /** 草图组在设计树中的显示名称。 */
    readonly name: string;
    /** 草图组在设计树中的稳定顺序；无法解析时为 null。 */
    readonly order: number | null;
    /** 草图组首次创建时间戳（毫秒）；用于设计树时间线排序。 */
    readonly createdAt: number | null;
    /** 运行时快照 ID。 */
    readonly runtimeSnapshotId: string;
    /** 捕获时的草图定义版本。 */
    readonly revision: number;
    /** 草图组内草图对象数量。 */
    readonly objectCount: number;
    /** 草图组捕获时的显示态，用于设计树操作 undo/redo 后同步树与图形区。 */
    readonly displayState: SketchGroupRuntimeDisplayState | null;
    /** 快照语义签名，用于过滤没有实际变化的点击。 */
    readonly signature: string;
    /** 快照是否允许持久化；运行时快照当前必须为 false。 */
    readonly persistent: false;
    /** 捕获时间戳。 */
    readonly capturedAt: number;
    /** 调试标签。 */
    readonly label: string;
}

/**
 * 草图组运行时历史记录。
 */
export interface SketchGroupRuntimeHistoryRecord {
    /** 草图组 ID。 */
    readonly sketchId: string;
    /** 用户可读标签。 */
    readonly label: string;
    /** 命令类型。 */
    readonly commandType: string;
    /** 变更前快照。 */
    readonly before: SketchGroupRuntimeHistorySnapshot;
    /** 变更后快照。 */
    readonly after: SketchGroupRuntimeHistorySnapshot;
    /**
     * 随本笔运行时事务一同提交的领域补丁（如删除实体联动的尺寸悬空迁移）。
     *
     * 说明：宿主必须把这些补丁与运行时快照补丁合成**同一笔** CadTransaction
     * （HIST-001），保证 undo/redo 原子恢复。
     */
    readonly extraPatches?: readonly EntityPatch[];
}

/**
 * 草图组运行时历史存储接口。
 */
export interface SketchGroupRuntimeHistoryStore {
    /**
     * 恢复运行时快照。
     *
     * @param snapshot 草图组运行时历史快照。
     */
    readonly restoreSketchGroupRuntimeSnapshot: (snapshot: SketchGroupRuntimeHistorySnapshot) => void;
    /**
     * 删除运行时草图组。
     *
     * @param sketchId 草图组 ID。
     */
    readonly deleteSketchGroupRuntimeSnapshot: (sketchId: string) => void;
}

/**
 * 判断两个草图组运行时快照是否语义相同。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 两个快照是否表示相同草图状态。
 */
export function areSketchGroupRuntimeSnapshotsEqual(
    left: SketchGroupRuntimeHistorySnapshot,
    right: SketchGroupRuntimeHistorySnapshot,
): boolean {
    return left.sketchId === right.sketchId
        && left.name === right.name
        && left.order === right.order
        && left.createdAt === right.createdAt
        && left.revision === right.revision
        && left.objectCount === right.objectCount
        && left.displayState === right.displayState
        && left.signature === right.signature;
}
